import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthStore } from '@auth/stores/auth.store';
import { HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { environment } from '@environments/environment';

/**
 * Interceptor para agregar el token de autenticación en las cabeceras de las peticiones HTTP
 * y redirigir al login cuando el servidor responde con 401 (sesión expirada o inválida).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const token = authStore.authToken();

  let apiHostname = '';
  try {
    apiHostname = new URL(environment.baseApi).hostname;
  } catch {
    apiHostname = '';
  }

  // Lista de URLs excluidas (login, login-google, forgot-password, reset-password)
  const excludedUrls = [
    '/v1/sesiones/iniciar-sesion',
    '/v1/sesiones/iniciar-sesion-google',
    '/v1/sesiones/recuperar-clave',
    '/v1/sesiones/restablecer-clave'
  ];

  const isExcluded = excludedUrls.some((url) => req.url.includes(url));

  /** Sin Bearer en S3 u otros hosts *.amazonaws.com que no son API Gateway (execute-api). */
  let skipBearer = false;
  try {
    const parsed = new URL(req.url);
    skipBearer =
      Boolean(apiHostname) &&
      parsed.hostname.endsWith('amazonaws.com') &&
      parsed.hostname !== apiHostname;
  } catch {
    skipBearer = Boolean(apiHostname) && req.url.includes('amazonaws.com') && !req.url.includes(apiHostname);
  }

  const outgoingReq =
    token && !isExcluded && !skipBearer
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(outgoingReq).pipe(
    catchError((error) => {
      if (!isExcluded && !skipBearer && error?.status === HttpStatusCode.Unauthorized) {
        authStore.logout();
        router.navigate(['/auth/login']);
      }
      return throwError(() => error);
    }),
  );
};
